import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { serviceOptions } from '../data/mock';
import { TLD_LIST } from '../data/tlds';

const createJsToken = () => window.btoa(`${Date.now()}.${Math.random().toString(36).slice(2, 18)}`);
const suspiciousNameTerms = ['admin', 'test', 'website owner', 'site owner', 'marketing', 'seo', 'sales', 'support', 'service team', 'business owner'];
const containsUrl = (value) => /(?:https?:\/\/|www\.|[a-z0-9][a-z0-9.-]+\.(?:com|net|org|ru|io|co|biz|info|site|online|agency|marketing|top|xyz|me|ly)\b)/iu.test(value);
const isValidEmail = (value) => /^\S+@\S+\.\S+$/.test(value);
const containsDisallowedMarkup = (value) => /<\s*script/i.test(value);

const isSuspiciousName = (value) => {
  const normalized = value.trim().toLowerCase();

  if (!normalized) {
    return false;
  }

  if (!/^[\p{L}\s'-]+$/u.test(value)) {
    return true;
  }

  if (normalized.replace(/-/g, ' ').split(/\s+/).filter(Boolean).length > 4) {
    return true;
  }

  return suspiciousNameTerms.some((term) => normalized.includes(term));
};

const isAllowedTld = (value) => {
  const lastDotIndex = value.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return false;
  }

  const tld = value.slice(lastDotIndex + 1).toLowerCase();

  return TLD_LIST.has(tld);
};

const AppointmentContactForm = ({ idPrefix = 'appointment-form' }) => {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get('service') || '';
  const [formData, setFormData] = useState(() => ({
    name: '',
    email: '',
    service: initialService,
    message: ''
  }));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    service: false,
    message: false
  });
  const [errors, setErrors] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [jsToken, setJsToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const resetTimeoutRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const validate = useCallback((values) => {
    const nextErrors = {};
    const name = values.name.trim();
    const email = values.email.trim();
    const message = values.message.trim();

    if (!name) {
      nextErrors.name = 'Введите имя';
    } else if (name.length < 2) {
      nextErrors.name = 'Имя слишком короткое';
    } else if (name.length > 100) {
      nextErrors.name = 'Имя слишком длинное';
    } else if (isSuspiciousName(name)) {
      nextErrors.name = 'Введите корректное имя';
    }

    if (!email) {
      nextErrors.email = 'Введите email';
    } else if (email.length > 254 || !isValidEmail(email)) {
      nextErrors.email = 'Введите корректный email';
    } else if (!isAllowedTld(email)) {
      nextErrors.email = 'Недопустимый домен email';
    }

    if (!values.service) {
      nextErrors.service = 'Выберите услугу';
    }

    if (!message) {
      nextErrors.message = 'Введите сообщение';
    } else if (message.length < 10) {
      nextErrors.message = 'Сообщение слишком короткое';
    } else if (message.length > 1000) {
      nextErrors.message = 'Сообщение слишком длинное';
    } else if (containsUrl(message)) {
      nextErrors.message = 'Ссылки в сообщении не допускаются';
    } else if (containsDisallowedMarkup(message)) {
      nextErrors.message = 'Удалите HTML/скрипты из сообщения';
    }

    if (honeypot) {
      nextErrors.form = 'Проверка не пройдена';
    }

    if (turnstileSiteKey && !captchaToken) {
      nextErrors.captcha = 'Подтвердите, что вы не робот';
    }

    return nextErrors;
  }, [captchaToken, honeypot, turnstileSiteKey]);

  const visibleErrors = {
    name: touched.name ? errors.name : '',
    email: touched.email ? errors.email : '',
    service: touched.service ? errors.service : '',
    message: touched.message ? errors.message : '',
    form: errors.form || errors.rateLimit || ''
  };

  const renderTurnstile = useCallback(() => {
    if (!turnstileSiteKey || !turnstileRef.current || !window.turnstile) {
      return false;
    }

    if (turnstileWidgetId.current !== null) {
      return true;
    }

    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: turnstileSiteKey,
      callback: (token) => setCaptchaToken(token),
      'expired-callback': () => setCaptchaToken(''),
      'error-callback': () => setCaptchaToken('')
    });

    return true;
  }, [turnstileSiteKey]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setJsToken(createJsToken());
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const tryRender = () => {
      if (cancelled) {
        return;
      }

      if (renderTurnstile()) {
        return;
      }

      if (attempts < 50) {
        attempts += 1;
        setTimeout(tryRender, 200);
      }
    };

    tryRender();

    return () => {
      cancelled = true;

      if (turnstileWidgetId.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [renderTurnstile, turnstileSiteKey]);

  useEffect(() => {
    const service = searchParams.get('service') || '';

    if (!service) {
      return;
    }

    setFormData((prev) => (prev.service === service ? prev : { ...prev, service }));
  }, [searchParams]);

  useEffect(() => () => {
    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
    }
  }, []);

  const resetForm = useCallback(() => {
    setIsSubmitted(false);
    setFormData({ name: '', email: '', service: '', message: '' });
    setTouched({ name: false, email: false, service: false, message: false });
    setErrors({});
    setHoneypot('');
    setFormStartedAt(Date.now());
    setJsToken('');
    window.setTimeout(() => {
      setJsToken(createJsToken());
    }, 1200);
    setCaptchaToken('');

    if (turnstileWidgetId.current !== null && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate(formData);
    setErrors(nextErrors);
    setTouched({ name: true, email: true, service: true, message: true });

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const contactEndpoint =
        process.env.REACT_APP_CONTACT_ENDPOINT ||
        `${process.env.REACT_APP_API_BASE_URL || ''}/contact.php`;

      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: '',
          email: formData.email.trim(),
          service: formData.service,
          message: formData.message.trim(),
          website: honeypot,
          formStartedAt,
          jsToken,
          turnstileToken: captchaToken
        })
      });

      if (!response.ok) {
        let payload = {};

        try {
          payload = await response.json();
        } catch {
          payload = {};
        }

        setErrors({
          form: payload.detail || 'Не удалось отправить форму. Попробуйте позже.'
        });
        return;
      }

      setIsSubmitted(true);
      resetTimeoutRef.current = window.setTimeout(resetForm, 5000);
    } catch {
      setErrors({ form: 'Не удалось отправить форму. Проверьте соединение.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-sand-200 rounded-2xl p-8 shadow-lg">
      {isSubmitted ? (
        <div className="h-full flex flex-col items-center justify-center text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h3 className="text-2xl font-serif text-stone-800 mb-2">Спасибо!</h3>
          <p className="text-stone-600">Я свяжусь с вами в ближайшее время</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <div className="hidden">
            <label htmlFor={`${idPrefix}-website`}>Website</label>
            <input
              id={`${idPrefix}-website`}
              name="website"
              type="text"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
          <input type="hidden" name="formStartedAt" value={formStartedAt} />
          <input type="hidden" name="jsToken" value={jsToken} />
          <div>
            <label className="block text-stone-700 text-sm font-medium mb-2">Ваше имя *</label>
            <Input
              type="text"
              placeholder="Как к вам обращаться?"
              value={formData.name}
              onChange={(event) => handleChange('name', event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
              aria-required="true"
              maxLength={100}
              aria-invalid={visibleErrors.name ? 'true' : 'false'}
              aria-describedby={visibleErrors.name ? `${idPrefix}-name-error` : undefined}
              className={`w-full px-4 py-3 rounded-lg ${visibleErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            {visibleErrors.name && (
              <p id={`${idPrefix}-name-error`} className="mt-2 text-sm text-red-600" role="alert">
                {visibleErrors.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-stone-700 text-sm font-medium mb-2">Email *</label>
            <Input
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(event) => handleChange('email', event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              aria-required="true"
              maxLength={254}
              aria-invalid={visibleErrors.email ? 'true' : 'false'}
              aria-describedby={visibleErrors.email ? `${idPrefix}-email-error` : undefined}
              className={`w-full px-4 py-3 rounded-lg ${visibleErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            {visibleErrors.email && (
              <p id={`${idPrefix}-email-error`} className="mt-2 text-sm text-red-600" role="alert">
                {visibleErrors.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-stone-700 text-sm font-medium mb-2">Услуга *</label>
            <Select
              value={formData.service}
              onValueChange={(value) => {
                handleChange('service', value);
                setTouched((prev) => ({ ...prev, service: true }));
              }}
            >
              <SelectTrigger
                className={`w-full px-4 py-3 rounded-lg ${visibleErrors.service ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                aria-invalid={visibleErrors.service ? 'true' : 'false'}
                aria-describedby={visibleErrors.service ? `${idPrefix}-service-error` : undefined}
              >
                <SelectValue placeholder="Выберите услугу" />
              </SelectTrigger>
              <SelectContent>
                {serviceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {visibleErrors.service && (
              <p id={`${idPrefix}-service-error`} className="mt-2 text-sm text-red-600" role="alert">
                {visibleErrors.service}
              </p>
            )}
          </div>

          <div>
            <label className="block text-stone-700 text-sm font-medium mb-2">Сообщение *</label>
            <Textarea
              placeholder="Опишите кратко ваш запрос или задайте свой вопрос..."
              value={formData.message}
              onChange={(event) => handleChange('message', event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, message: true }))}
              rows={4}
              aria-required="true"
              maxLength={1000}
              aria-invalid={visibleErrors.message ? 'true' : 'false'}
              aria-describedby={visibleErrors.message ? `${idPrefix}-message-error` : undefined}
              className={`w-full px-4 py-3 rounded-lg resize-none ${visibleErrors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            {visibleErrors.message && (
              <p id={`${idPrefix}-message-error`} className="mt-2 text-sm text-red-600" role="alert">
                {visibleErrors.message}
              </p>
            )}
          </div>

          {turnstileSiteKey && (
            <div>
              <div ref={turnstileRef} />
              {errors.captcha && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {errors.captcha}
                </p>
              )}
            </div>
          )}

          {visibleErrors.form && (
            <p className="text-sm text-red-600" role="alert">
              {visibleErrors.form}
            </p>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 rounded-full text-base font-medium"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Отправка...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={18} />
                Отправить заявку
              </span>
            )}
          </Button>
        </form>
      )}
    </div>
  );
};

export default AppointmentContactForm;
